/**
 * useBooking - Booking Orchestration Hook
 *
 * 1. POST /api/public/bookings. From contract v1.3 this creates a booking
 *    request (status 'scheduled') that the owner confirms in /admin; an
 *    older backend answered 'confirmed'. The confirmation page reads
 *    `status` from the stored summary and words itself for either.
 * 2. Only when the booking response says payments_enabled AND
 *    payment_status 'pending' (contract §3.4), start an online payment.
 *    The response decides, not the build-time REACT_APP_PAYMENTS_ENABLED
 *    flag (that only drives checkout copy), so the two can never drift
 *    into "pending but nobody asked to pay". At launch (pay at session)
 *    the booking is simply returned.
 * 3. Keep a PII-free summary in sessionStorage for the confirmation page.
 *
 * No isMountedRef: React 18 does not warn about state updates after
 * unmount, and the old ref stayed false after StrictMode's dev remount, so
 * createBooking returned null. The returned promise always settles.
 *
 * @example
 * const { createBooking, loading } = useBooking();
 * const booking = await createBooking(formValues, selectedSlot, serviceSlug);
 * if (booking.payment) redirectToPayment(booking.payment);
 * else navigate('/booking-confirmation');
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createBooking as createBookingAPI } from '../services/api/bookingService';
import { initiatePayment } from '../services/api/paymentService';
import { getErrorMessage, isSlotUnavailableError } from '../services/errors/errorHandler';
import { getServiceSlug } from '../services/utils/serviceMapper';
import { clearAvailabilityCache } from './useAvailability';

/** sessionStorage key read by the confirmation page. */
export const STORAGE_KEY_BOOKING = 'current_booking';

/**
 * The only booking fields kept in the browser: no client name, email,
 * phone or notes.
 * @param {object} booking - Normalized booking from bookingService
 * @returns {object}
 */
export const toStoredBooking = (booking) => ({
  bookingReference: booking.bookingReference,
  serviceName: booking.serviceName,
  startISO: booking.startISO,
  endISO: booking.endISO,
  amount: booking.amount,
  currency: booking.currency,
  paymentStatus: booking.paymentStatus,
  status: booking.status,
  meetingUrl: booking.meetingUrl,
});

const storeBooking = (booking) => {
  try {
    sessionStorage.setItem(STORAGE_KEY_BOOKING, JSON.stringify(toStoredBooking(booking)));
  } catch (error) {
    // Storage unavailable; the confirmation page shows its "not found" state.
  }
};

const retrieveBooking = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY_BOOKING);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

const clearStoredBooking = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY_BOOKING);
  } catch (error) {
    // Nothing stored.
  }
};

/**
 * @param {object} [options]
 * @param {Function} [options.onSuccess] - Called with the booking result
 * @param {Function} [options.onError] - Called with the thrown error
 * @returns {{createBooking: Function, loading: boolean, error: string|null, errorCode: string|null, currentBooking: object|null, reset: Function, getStoredBooking: Function, hasBookingInProgress: Function}}
 */
const useBooking = (options = {}) => {
  const { onSuccess = null, onError = null } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [currentBooking, setCurrentBooking] = useState(null);

  // One submission at a time: a second call while one is pending gets the same promise.
  const pendingRef = useRef(null);

  // Keep the latest callbacks without re-creating createBooking.
  const callbacksRef = useRef({ onSuccess, onError });
  callbacksRef.current = { onSuccess, onError };

  useEffect(() => {
    const stored = retrieveBooking();
    if (stored) {
      setCurrentBooking(stored);
    }
  }, []);

  /**
   * Create a booking and, only if payments are enabled and pending, start payment.
   *
   * @param {object} form - {firstName, lastName, email, phone?, notes?, policyAccepted, website?}
   * @param {{startISO: string, endISO: string}} slot - Slot from useAvailability
   * @param {string|{slug: string}} service - Service slug (or service object)
   * @returns {Promise<object>} Normalized booking plus
   *   `payment` ({url, formData} or null) and `paymentError` (string or null)
   * @throws {ApiError|Error} Booking failed; nothing was created
   */
  const createBooking = useCallback((form, slot, service) => {
    if (pendingRef.current) {
      return pendingRef.current;
    }

    const run = async () => {
      setLoading(true);
      setError(null);
      setErrorCode(null);

      try {
        const booking = await createBookingAPI(form, slot, service);

        let payment = null;
        let paymentError = null;
        if (booking.paymentsEnabled && booking.paymentStatus === 'pending') {
          try {
            payment = await initiatePayment(booking.bookingReference);
          } catch (paymentErr) {
            // The booking exists; only the online payment failed.
            paymentError =
              `Your booking was saved (reference ${booking.bookingReference}), ` +
              `but online payment could not be started: ${getErrorMessage(paymentErr)}`;
          }
        }

        storeBooking(booking);
        setCurrentBooking(toStoredBooking(booking));

        const result = { ...booking, payment, paymentError };
        callbacksRef.current.onSuccess?.(result);
        return result;
      } catch (err) {
        if (isSlotUnavailableError(err)) {
          // The picker's cached slots are stale: force a refetch on next view.
          try {
            clearAvailabilityCache(getServiceSlug(service));
          } catch (e) {
            clearAvailabilityCache();
          }
        }
        setError(getErrorMessage(err));
        setErrorCode(err?.code || null);
        callbacksRef.current.onError?.(err);
        throw err;
      } finally {
        setLoading(false);
        pendingRef.current = null;
      }
    };

    pendingRef.current = run();
    return pendingRef.current;
  }, []);

  /** Clear hook state and the stored booking. */
  const reset = useCallback(() => {
    setCurrentBooking(null);
    setError(null);
    setErrorCode(null);
    setLoading(false);
    clearStoredBooking();
  }, []);

  const getStoredBooking = useCallback(() => retrieveBooking(), []);

  const hasBookingInProgress = useCallback(() => !!retrieveBooking(), []);

  return {
    createBooking,
    loading,
    error,
    errorCode,
    currentBooking,
    reset,
    getStoredBooking,
    hasBookingInProgress,
  };
};

/** Clear the stored booking summary (exported for cleanup). */
export const clearBookingStorage = clearStoredBooking;

export default useBooking;
